import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Divider, LinearProgress, Stack, Typography } from '@mui/material';
import { sessionAnalytics } from './api';

const formatPercent = (fraction) => `${Math.round(fraction * 100)}%`;

// The debrief a host reads once the room has cleared out. Fetched once on
// mount rather than over the SSE stream that drives the rest of this screen —
// the backend already refuses to answer before 'ended', so there's nothing
// here that would ever need to move live.
function SessionAnalytics({ sessionId }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        sessionAnalytics(sessionId)
            .then(setData)
            .catch(() => setError('Could not load session analytics.'));
    }, [sessionId]);

    if (error) return <Alert severity="error" sx={{ mt: 4, textAlign: 'left' }}>{error}</Alert>;
    if (!data) return <Box sx={{ mt: 4 }}><CircularProgress /></Box>;

    const durationMinutes = Math.round((new Date(data.endedAt) - new Date(data.createdAt)) / 60000);

    return (
        <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
                <Chip label={`${data.joinedCount} joined`} />
                <Chip label={`${data.questionCount} questions`} />
                <Chip label={`${durationMinutes} min`} />
                {data.accuracy !== null && (
                    <Chip color="success" label={`${formatPercent(data.accuracy)} correct overall`} />
                )}
            </Stack>

            <Stack spacing={3}>
                {data.questions.map((question) => (
                    <Box key={question.ord} data-testid={`analytics-question-${question.ord}`}>
                        <Typography sx={{ fontWeight: 700, mb: 1 }}>
                            Q{question.ord + 1}. {question.prompt}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            {question.answered} of {data.joinedCount} answered
                            {question.answered > 0 && ` · ${formatPercent(question.correctCount / question.answered)} correct`}
                        </Typography>

                        <Stack spacing={1}>
                            {question.options.map((option, index) => {
                                const isCorrect = index === question.correct;
                                const count = question.counts[index];
                                const pct = question.answered > 0 ? (count / question.answered) * 100 : 0;

                                return (
                                    <Box key={option}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography sx={{ fontWeight: isCorrect ? 700 : 400 }}>
                                                {option}{isCorrect && ' — correct answer'}
                                            </Typography>
                                            <Typography data-testid="option-count" color="text.secondary">{count}</Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            color={isCorrect ? 'success' : 'primary'}
                                            sx={{ height: 8, borderRadius: 1 }}
                                        />
                                    </Box>
                                );
                            })}
                        </Stack>

                        {question.ord < data.questions.length - 1 && <Divider sx={{ mt: 3 }} />}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

export default SessionAnalytics;
